import React, { useState } from "react";
import {
  Card,
  Avatar,
  Typography,
  Tag,
  Row,
  Col,
  Image,
  Spin,
  Empty,
  List,
  Button,
  Modal,
  Input,
  message,
} from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  HeartOutlined,
  PhoneOutlined,
  MailOutlined,
  CameraOutlined,
  GiftOutlined,
  TeamOutlined,
  ExclamationCircleOutlined,
  MessageOutlined,
  LockOutlined,
  UnlockOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import moment from "moment";
import {
  useGetUserProfileQuery,
  useGetDetailedReportsQuery,
  useBlockUserMutation,
  useUnblockUserMutation,
} from "../../../redux/rtk/apiRtk";
import { motion } from "framer-motion";
import { primaryColor } from "../../../utils/constants";

const { Title, Text, Paragraph } = Typography;

const UserProfileDetails = () => {
  const { id } = useParams();

  // API hook
  const { data: user, isLoading, error } = useGetUserProfileQuery(id);

  // Get detailed reports for this user
  const { data: reportsData, isLoading: reportsLoading } =
    useGetDetailedReportsQuery({
      type: "user",
      id: id,
    });

  // Block/Unblock mutations
  const [blockUser, { isLoading: isBlocking }] = useBlockUserMutation();
  const [unblockUser, { isLoading: isUnblocking }] = useUnblockUserMutation();

  // Modal state
  const [isBlockModalVisible, setIsBlockModalVisible] = useState(false);
  const [blockReason, setBlockReason] = useState("");

  // Block/Unblock handlers
  const handleBlockUser = async () => {
    if (!blockReason.trim()) {
      message.error("Please provide a reason for blocking this user");
      return;
    }

    try {
      await blockUser({ id: user?.user?._id, reason: blockReason }).unwrap();
      message.success("User has been blocked successfully");
      setIsBlockModalVisible(false);
      setBlockReason("");
    } catch (error) {
      message.error("Failed to block user. Please try again.");
      console.error("Block user error:", error);
    }
  };

  const handleUnblockUser = async () => {
    try {
      await unblockUser(user?.user?._id).unwrap();
      message.success("User has been unblocked successfully");
    } catch (error) {
      message.error("Failed to unblock user. Please try again.");
      console.error("Unblock user error:", error);
    }
  };

  console.log({ user }); // Debug: Check user data structure

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spin size="large" tip="Loading user profile..." />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Empty description="User not found" />
      </div>
    );
  }

  const InfoCard = ({
    title,
    icon,
    children,
    className = "",
    minHeight = "300px",
  }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="h-full"
    >
      <Card
        className={`shadow-lg rounded-xl h-full ${className}`}
        style={{
          boxShadow: "0 4px 20px rgba(164, 1, 101, 0.1)",
          minHeight: minHeight,
          display: "flex",
          flexDirection: "column",
        }}
        bodyStyle={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="flex items-center mb-4">
          <div
            className="flex items-center justify-center w-8 h-8 rounded-full mr-3"
            style={{ background: `${primaryColor}15` }}
          >
            {React.cloneElement(icon, { style: { color: primaryColor } })}
          </div>
          <Title level={5} style={{ margin: 0, color: primaryColor }}>
            {title}
          </Title>
        </div>
        <div className="flex-1">{children}</div>
      </Card>
    </motion.div>
  );

  const formatIssueForDisplay = (issue) => {
    if (!issue) return "No details provided";
    if (typeof issue === "string") return issue;

    if (Array.isArray(issue)) {
      const normalized = issue
        .map((item) =>
          typeof item === "string" ? item : JSON.stringify(item)
        )
        .filter(Boolean);
      return normalized.length > 0 ? normalized.join(", ") : "No details provided";
    }

    if (typeof issue === "object") {
      const parts = [];

      if (issue.category) parts.push(`Category: ${issue.category}`);
      if (issue.when) parts.push(`When: ${issue.when}`);
      if (issue.where) parts.push(`Where: ${issue.where}`);

      if (parts.length > 0) {
        return parts.join(" | ");
      }

      return JSON.stringify(issue);
    }

    return String(issue);
  };

  const formatOfferForDisplay = (offer) => {
    if (!offer) return "No details provided";
    if (typeof offer === "string") return offer;

    if (Array.isArray(offer)) {
      return offer
        .map((item) => formatOfferForDisplay(item))
        .filter(Boolean)
        .join(", ");
    }

    if (typeof offer === "object") {
      const category = offer.category || "Not specified";
      const when = Array.isArray(offer.when) ? offer.when : [];
      const where = Array.isArray(offer.where) ? offer.where : [];

      const whenText =
        when.length > 0
          ? when
              .map((slot) => {
                if (typeof slot === "string") return slot;
                if (slot && typeof slot === "object") {
                  return slot.name || slot.label || JSON.stringify(slot);
                }
                return String(slot);
              })
              .join(", ")
          : "Any time";

      const whereText =
        where.length > 0
          ? where
              .map((place) => {
                if (typeof place === "string") return place;
                if (place && typeof place === "object") {
                  return place.name || place.placeId || JSON.stringify(place);
                }
                return String(place);
              })
              .join(", ")
          : "Any place";

      return `Category: ${category} | When: ${whenText} | Where: ${whereText}`;
    }

    return String(offer);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header Card */}
        <Card
          className="mb-6 shadow-lg rounded-xl"
          style={{ boxShadow: "0 4px 20px rgba(164, 1, 101, 0.1)" }}
        >
          <div className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  {user.profilePic?.url ? (
                    <Avatar
                      size={80}
                      src={user.profilePic.url}
                      className="border-4 border-white shadow-lg"
                      style={{ boxShadow: `0 0 0 3px ${primaryColor}30` }}
                    />
                  ) : (
                    <Avatar
                      size={80}
                      icon={<UserOutlined />}
                      className="border-4 border-white shadow-lg"
                      style={{
                        backgroundColor: `${primaryColor}50`,
                        color: primaryColor,
                      }}
                    />
                  )}
                  {/* Online Status Indicator */}
                  {user.user?.isOnline && (
                    <div
                      className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-3 border-white flex items-center justify-center"
                      style={{ backgroundColor: "#52c41a" }}
                    >
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <Title level={3} style={{ margin: 0, color: primaryColor }}>
                      {user.firstName} {user.lastName}
                    </Title>
                    {user.user?.isInChat && (
                      <Tag color="blue" className="animate-pulse">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          In Chat
                        </div>
                      </Tag>
                    )}
                  </div>
                  {user.about && (
                    <Paragraph
                      type="secondary"
                      style={{ margin: "8px 0", maxWidth: "400px" }}
                    >
                      {user.about}
                    </Paragraph>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {user.jobTitle && (
                      <Tag
                        className="px-3 py-1"
                        style={{
                          backgroundColor: `${primaryColor}15`,
                          color: primaryColor,
                          border: `1px solid ${primaryColor}30`,
                          borderRadius: "20px",
                          fontWeight: "500",
                        }}
                      >
                        💼 {user.jobTitle}
                      </Tag>
                    )}
                    {user.company && (
                      <Tag
                        className="px-3 py-1"
                        style={{
                          backgroundColor: `${primaryColor}08`,
                          color: primaryColor,
                          border: `1px solid ${primaryColor}15`,
                          borderRadius: "20px",
                          fontWeight: "500",
                        }}
                      >
                        🏢 {user.company}
                      </Tag>
                    )}
                    {user.city && (
                      <Tag
                        className="px-3 py-1"
                        style={{
                          backgroundColor: "#f0f9ff",
                          color: "#0369a1",
                          border: "1px solid #bae6fd",
                          borderRadius: "20px",
                          fontWeight: "500",
                        }}
                      >
                        📍 {user.city}
                      </Tag>
                    )}
                  </div>
                </div>
              </div>

              <div className="ml-auto flex flex-col items-end gap-3">
                {/* Account Status Badges */}
                <div className="flex flex-col gap-2 items-end">
                  <div className="flex items-center gap-2">
                    <Tag
                      color={user.user?.blocked ? "red" : "green"}
                      className="px-3 py-1 font-medium"
                      style={{ borderRadius: "20px" }}
                    >
                      {user.user?.blocked ? "🚫 Blocked" : "✅ Active"}
                    </Tag>
                    <Tag
                      color={user.user?.verified ? "green" : "orange"}
                      className="px-3 py-1 font-medium"
                      style={{ borderRadius: "20px" }}
                    >
                      {user.user?.verified ? "✅ Verified" : "⏳ Unverified"}
                    </Tag>
                  </div>

                  {/* Block/Unblock Actions */}
                  <div className="flex items-center gap-2 mt-2">
                    {user.user?.blocked ? (
                      <Button
                        type="primary"
                        icon={<UnlockOutlined />}
                        onClick={handleUnblockUser}
                        loading={isUnblocking}
                        style={{
                          backgroundColor: "#52c41a",
                          borderColor: "#52c41a",
                        }}
                      >
                        Unblock User
                      </Button>
                    ) : (
                      <Button
                        danger
                        icon={<LockOutlined />}
                        onClick={() => setIsBlockModalVisible(true)}
                        loading={isBlocking}
                      >
                        Block User
                      </Button>
                    )}
                  </div>
                </div>

                {/* Coins and Join Date */}
                <div className="text-right">
                  <div className="flex items-center justify-end mb-2">
                    <GiftOutlined
                      style={{
                        color: primaryColor,
                        marginRight: 8,
                        fontSize: "18px",
                      }}
                    />
                    <Text
                      strong
                      style={{ color: primaryColor, fontSize: "16px" }}
                    >
                      {user.coins || 0} Coins
                    </Text>
                  </div>
                  <Text
                    type="secondary"
                    className="flex items-center justify-end"
                  >
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    Joined {moment(user.createdAt).format("MMM Do, YYYY")}
                  </Text>
                </div>
              </div>
            </div>

            {/* Role Section - Subtle Design */}
            <div
              className="mt-6 pt-4 border-t"
              style={{ borderColor: `${primaryColor}15` }}
            >
              <div
                className="flex items-center justify-center gap-3 py-3 px-4 rounded-lg"
                style={{
                  backgroundColor: `${primaryColor}05`,
                  border: `1px solid ${primaryColor}10`,
                }}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="flex items-center justify-center w-8 h-8 rounded-full"
                    style={{ backgroundColor: `${primaryColor}15` }}
                  >
                    <TeamOutlined
                      style={{ color: `${primaryColor}80`, fontSize: "14px" }}
                    />
                  </div>
                  <Text
                    style={{
                      color: `${primaryColor}CC`,
                      fontSize: "14px",
                      fontWeight: "500",
                      textTransform: "capitalize",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {user.user?.role || "User"} Account
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Reports Count Section */}
        {reportsData && reportsData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-6"
          >
            <Card
              className="shadow-lg rounded-xl"
              style={{ boxShadow: "0 4px 20px rgba(164, 1, 101, 0.1)" }}
            >
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100">
                      <ExclamationCircleOutlined className="text-red-600 text-xl" />
                    </div>
                    <div>
                      <Text strong className="text-red-800 text-lg block">
                        {
                          new Set(
                            reportsData.map((report) => report.submitter._id)
                          ).size
                        }{" "}
                        Unique Reporter
                        {new Set(
                          reportsData.map((report) => report.submitter._id)
                        ).size !== 1
                          ? "s"
                          : ""}
                      </Text>
                      <Text type="secondary" className="text-sm">
                        {reportsData.length} total report
                        {reportsData.length !== 1 ? "s" : ""} filed against this
                        user
                      </Text>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Tag color="red" className="font-medium px-3 py-1">
                      Requires Attention
                    </Tag>
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        <Row gutter={[24, 24]}>
          {/* Contact Information */}
          <Col xs={24} lg={8}>
            <InfoCard title="Contact Information" icon={<MailOutlined />}>
              <div className="space-y-4">
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <div className="flex items-center">
                    <MailOutlined
                      style={{ color: primaryColor, marginRight: 8 }}
                    />
                    <Text type="secondary">Email:</Text>
                  </div>
                  <Text strong>{user.user?.email || "Not specified"}</Text>
                </div>
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <div className="flex items-center">
                    <PhoneOutlined
                      style={{ color: primaryColor, marginRight: 8 }}
                    />
                    <Text type="secondary">Phone:</Text>
                  </div>
                  <Text strong>
                    {user.user?.phone && user.user.phone !== 0
                      ? user.user.phone
                      : "Not specified"}
                  </Text>
                </div>
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <div className="flex items-center">
                    <UserOutlined
                      style={{ color: primaryColor, marginRight: 8 }}
                    />
                    <Text type="secondary">Gender:</Text>
                  </div>
                  <Text strong className="capitalize">
                    {user.gender || "Not specified"}
                  </Text>
                </div>
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <div className="flex items-center">
                    <CalendarOutlined
                      style={{ color: primaryColor, marginRight: 8 }}
                    />
                    <Text type="secondary">Age:</Text>
                  </div>
                  <Text strong>
                    {user.dob
                      ? `${moment().diff(moment(user.dob), "years")} years`
                      : "Not specified"}
                  </Text>
                </div>
              </div>
            </InfoCard>
          </Col>

          {/* Personal Details */}
          <Col xs={24} lg={8}>
            <InfoCard title="Personal Details" icon={<UserOutlined />}>
              <div className="space-y-4">
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <Text type="secondary">Date of Birth:</Text>
                  <Text strong>
                    {user.dob
                      ? moment(user.dob).format("MMM Do, YYYY")
                      : "Not specified"}
                  </Text>
                </div>
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <Text type="secondary">Height:</Text>
                  <Text strong>{user.height || "Not specified"}</Text>
                </div>
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <Text type="secondary">School:</Text>
                  <Text strong>{user.school || "Not specified"}</Text>
                </div>
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <Text type="secondary">Company:</Text>
                  <Text strong>{user.company || "Not specified"}</Text>
                </div>
              </div>
            </InfoCard>
          </Col>

          {/* Account Status */}
          <Col xs={24} lg={8}>
            <InfoCard title="Account Status" icon={<TeamOutlined />}>
              <div className="space-y-4">
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <Text type="secondary">Online Status:</Text>
                  <Tag color={user.user?.isOnline ? "green" : "red"}>
                    {user.user?.isOnline ? "Online" : "Offline"}
                  </Tag>
                </div>
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <Text type="secondary">In Chat:</Text>
                  <Tag color={user.user?.isInChat ? "blue" : "default"}>
                    {user.user?.isInChat ? "In Chat" : "Not in Chat"}
                  </Tag>
                </div>
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <Text type="secondary">Account Status:</Text>
                  <Tag color={user.user?.blocked ? "red" : "green"}>
                    {user.user?.blocked ? "Blocked" : "Active"}
                  </Tag>
                </div>
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <Text type="secondary">Verified:</Text>
                  <Tag color={user.user?.verified ? "green" : "orange"}>
                    {user.user?.verified ? "Verified" : "Unverified"}
                  </Tag>
                </div>
              </div>
            </InfoCard>
          </Col>

          {/* Location Information */}
          <Col xs={24} lg={12}>
            <InfoCard
              title="Location & Preferences"
              icon={<EnvironmentOutlined />}
            >
              <div className="space-y-4">
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <div className="flex items-center">
                    <EnvironmentOutlined
                      style={{ color: primaryColor, marginRight: 8 }}
                    />
                    <Text type="secondary">City:</Text>
                  </div>
                  <Text strong>{user.city || "Not specified"}</Text>
                </div>
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <Text type="secondary">Max Distance:</Text>
                  <Text strong>
                    {user.maxDistance
                      ? `${user.maxDistance} km`
                      : "Not specified"}
                  </Text>
                </div>
                {user.location && (
                  <>
                    <div
                      className="flex items-center justify-between p-3 rounded-lg"
                      style={{ backgroundColor: `${primaryColor}05` }}
                    >
                      <Text type="secondary">Latitude:</Text>
                      <Text strong>{user.location.latitude}</Text>
                    </div>
                    <div
                      className="flex items-center justify-between p-3 rounded-lg"
                      style={{ backgroundColor: `${primaryColor}05` }}
                    >
                      <Text type="secondary">Longitude:</Text>
                      <Text strong>{user.location.longitude}</Text>
                    </div>
                  </>
                )}
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <Text type="secondary">Profile Visibility:</Text>
                  <Tag color={user.hideprofile ? "red" : "green"}>
                    {user.hideprofile ? "Hidden" : "Visible"}
                  </Tag>
                </div>
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <Text type="secondary">Notifications:</Text>
                  <Tag color={user.notification ? "green" : "red"}>
                    {user.notification ? "Enabled" : "Disabled"}
                  </Tag>
                </div>
              </div>
            </InfoCard>
          </Col>

          {/* Dating Preferences */}
          <Col xs={24} lg={12}>
            <InfoCard title="Dating Preferences" icon={<HeartOutlined />}>
              <div className="space-y-4">
                <div
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <Text type="secondary" className="block mb-3">
                    Show Me:
                  </Text>
                  <div className="flex flex-wrap gap-2">
                    {user.showMe && user.showMe.length > 0 ? (
                      user.showMe.map((pref, index) => (
                        <Tag
                          key={index}
                          className="capitalize"
                          style={{
                            backgroundColor: `${primaryColor}15`,
                            color: primaryColor,
                            border: `1px solid ${primaryColor}30`,
                          }}
                        >
                          {pref}
                        </Tag>
                      ))
                    ) : (
                      <Text>Not specified</Text>
                    )}
                  </div>
                </div>
                {user.ageRange && (
                  <div
                    className="flex items-center justify-between p-3 rounded-lg"
                    style={{ backgroundColor: `${primaryColor}05` }}
                  >
                    <Text type="secondary">Age Range:</Text>
                    <Text strong>
                      {user.ageRange.from} - {user.ageRange.to} years
                    </Text>
                  </div>
                )}
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: `${primaryColor}05` }}
                >
                  <div className="flex items-center">
                    <GiftOutlined
                      style={{ color: primaryColor, marginRight: 8 }}
                    />
                    <Text type="secondary">Coins:</Text>
                  </div>
                  <Text strong style={{ color: primaryColor }}>
                    {user.coins || 0} Coins
                  </Text>
                </div>
                {user.expoPushToken && (
                  <div
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: `${primaryColor}05` }}
                  >
                    <Text type="secondary" className="block mb-2">
                      Push Token:
                    </Text>
                    <Text
                      className="text-xs break-all"
                      style={{ color: primaryColor }}
                    >
                      {user.expoPushToken}
                    </Text>
                  </div>
                )}
              </div>
            </InfoCard>
          </Col>

          {/* Offers */}
          {user.offers && user.offers.length > 0 && (
            <Col xs={24}>
              <InfoCard title="Offers" icon={<GiftOutlined />}>
                <div className="grid gap-3">
                  {user.offers.map((offer, index) => (
                    <div
                      key={index}
                      className="p-3 rounded-lg"
                      style={{ backgroundColor: `${primaryColor}08` }}
                    >
                      <Text>{formatOfferForDisplay(offer)}</Text>
                    </div>
                  ))}
                </div>
              </InfoCard>
            </Col>
          )}

          {/* Gallery */}
          {user.gallery && user.gallery.length > 0 && (
            <Col xs={24}>
              <InfoCard title="Gallery" icon={<CameraOutlined />}>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {user.gallery.map((imageUrl, index) => (
                    <div key={index} className="aspect-square">
                      <Image
                        src={imageUrl}
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                        style={{ aspectRatio: "1/1" }}
                      />
                    </div>
                  ))}
                </div>
              </InfoCard>
            </Col>
          )}
        </Row>

        {/* Reports Section - Right after profile header */}
        {reportsData && reportsData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="my-6"
          >
            <Card
              className="shadow-lg rounded-xl"
              style={{ boxShadow: "0 4px 20px rgba(164, 1, 101, 0.1)" }}
              title={
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full mr-3"
                      style={{ background: `${primaryColor}15` }}
                    >
                      <ExclamationCircleOutlined
                        style={{ color: primaryColor }}
                      />
                    </div>
                    <span>Reports Against This User</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Text strong className="text-red-700">
                      {
                        new Set(
                          reportsData.map((report) => report.submitter._id)
                        ).size
                      }{" "}
                      Reporter
                      {new Set(
                        reportsData.map((report) => report.submitter._id)
                      ).size !== 1
                        ? "s"
                        : ""}
                      , {reportsData.length} Report
                      {reportsData.length !== 1 ? "s" : ""} Filed
                    </Text>
                  </div>
                </div>
              }
              headStyle={{ borderBottom: `1px solid ${primaryColor}20` }}
              bodyStyle={{ padding: "20px" }}
            >
              {reportsLoading ? (
                <div className="flex justify-center items-center py-8">
                  <Spin size="large" tip="Loading reports..." />
                </div>
              ) : (
                <List
                  itemLayout="vertical"
                  dataSource={reportsData}
                  renderItem={(report, index) => (
                    <List.Item
                      key={report._id}
                      className="border-b border-gray-100 last:border-b-0 pb-4 mb-4 last:mb-0"
                    >
                      <div className="flex items-start space-x-4">
                        {/* Reporter Avatar */}
                        <Avatar
                          size={48}
                          src={
                            report.submitter?.profilePic?.secure_url ||
                            report.submitter?.profilePic?.url
                          }
                          icon={<UserOutlined />}
                          className="border-2 border-white shadow-sm flex-shrink-0"
                          style={{ boxShadow: `0 0 0 2px ${primaryColor}30` }}
                        />

                        {/* Report Content */}
                        <div className="flex-1 min-w-0">
                          {/* Reporter Info */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Text strong className="text-gray-800">
                                {report.submitter?.firstName}{" "}
                                {report.submitter?.lastName}
                              </Text>
                              <Tag
                                size="small"
                                className="text-xs"
                                style={{
                                  backgroundColor: "#ff4d4f15",
                                  color: "#ff4d4f",
                                  border: "1px solid #ff4d4f30",
                                }}
                              >
                                Report #{index + 1}
                              </Tag>
                            </div>

                            {/* Report Date */}
                            <div className="flex items-center text-gray-500 text-sm">
                              <CalendarOutlined className="mr-1" />
                              {new Date(report.createdAt).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </div>
                          </div>

                          {/* Report Issue */}
                          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                            <div className="flex items-start space-x-2">
                              <MessageOutlined
                                className="text-gray-400 mt-1 flex-shrink-0"
                                style={{ fontSize: "14px" }}
                              />
                              <Text className="text-gray-700 leading-relaxed">
                                {formatIssueForDisplay(report.issue)}
                              </Text>
                            </div>
                          </div>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </motion.div>
        )}
      </motion.div>

      {/* Block User Modal */}
      <Modal
        title={
          <div className="flex items-center">
            <StopOutlined className="text-red-500 mr-2" />
            <span>Block User</span>
          </div>
        }
        open={isBlockModalVisible}
        onOk={handleBlockUser}
        onCancel={() => {
          setIsBlockModalVisible(false);
          setBlockReason("");
        }}
        confirmLoading={isBlocking}
        okText="Block User"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        width={500}
      >
        <div className="py-4">
          <p className="mb-4 text-gray-600">
            Are you sure you want to block{" "}
            <strong>
              {user?.firstName} {user?.lastName}
            </strong>
            ? This action will prevent them from accessing their account and
            using the platform.
          </p>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for blocking <span className="text-red-500">*</span>
            </label>
            <Input.TextArea
              placeholder="Please provide a detailed reason for blocking this user..."
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              rows={4}
              maxLength={500}
              showCount
            />
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start">
              <ExclamationCircleOutlined className="text-red-500 mt-1 mr-2" />
              <div className="text-sm text-red-700">
                <strong>Warning:</strong> Blocking this user will immediately
                restrict their access to the platform. They will be notified of
                this action and the reason provided.
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Custom styles for consistent card heights and smooth appearance */}
      <style jsx="true">{`
        .ant-card {
          transition: all 0.3s ease;
        }

        .ant-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(164, 1, 101, 0.15) !important;
        }

        .ant-row {
          align-items: stretch;
        }

        .ant-col {
          display: flex;
          flex-direction: column;
        }

        .space-y-4 > * + * {
          margin-top: 1rem;
        }

        .rounded-lg {
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .rounded-lg:hover {
          background-color: ${primaryColor}08 !important;
        }

        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .border-3 {
          border-width: 3px;
        }

        /* Role banner gradient animation */
        .role-banner {
          background: linear-gradient(
            135deg,
            ${primaryColor} 0%,
            ${primaryColor}CC 100%
          );
          position: relative;
          overflow: hidden;
        }

        .role-banner::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.1),
            transparent
          );
          animation: shimmer 3s infinite;
        }

        @keyframes shimmer {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }

        .ant-list-item:hover {
          background-color: ${primaryColor}02 !important;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .ant-empty-img-simple-ellipse {
          fill: ${primaryColor}20 !important;
        }

        .ant-empty-img-simple-path {
          fill: ${primaryColor}40 !important;
        }
      `}</style>
    </div>
  );
};

export default UserProfileDetails;
